import { createContext, useContext, ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import type { ApiResponse } from '../types/task';

interface BrandingResponse {
  logoUrl: string | null;
  logoS3Key: string | null;
  orgName: string | null;
  defaultLandingPage: string | null;
}

interface BrandingContextValue {
  logoUrl: string | null;
  orgName: string | null;
  /** Admin-configured route everyone lands on at "/" (null = "/home"). */
  defaultLandingPage: string | null;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

export const BRANDING_QUERY_KEY = ['system', 'branding'];

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<ApiResponse<BrandingResponse>>('/system/branding');
      return res.data!;
    },
    staleTime: 1000 * 60 * 10,
  });

  const value = useMemo<BrandingContextValue>(
    () => ({
      logoUrl: data?.logoUrl ?? null,
      orgName: data?.orgName ?? null,
      defaultLandingPage: data?.defaultLandingPage ?? null,
      isLoading,
    }),
    [data?.logoUrl, data?.orgName, data?.defaultLandingPage, isLoading],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}
